import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiPost } from "./api";
import { fetchCurrentUser } from "./user";

/**
 * Three states, not two.
 *
 * The session cookie is httpOnly now, so there is nothing for JS to read synchronously -
 * whether the user is signed in is only knowable once /me answers. A plain boolean would
 * have to start `false`, and RequireAuth would bounce a genuinely signed-in user off
 * /dashboard on every refresh before the answer arrived. `loading` is what stops that.
 */
export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthState {
    status: AuthStatus;
    isLoggedIn: boolean;
    username: string | null;
    profilePictureUrl: string | null;
    /** Called after login/signup have already established the cookie server-side. */
    onAuthenticated: () => void;
    logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [status, setStatus] = useState<AuthStatus>("loading");
    const [username, setUsername] = useState<string | null>(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
    // Bumped to force a re-check after signing in.
    const [refresh, setRefresh] = useState(0);

    const onAuthenticated = useCallback(() => {
        setStatus("loading");
        setRefresh(n => n + 1);
    }, []);

    const logout = useCallback(async () => {
        try {
            // The cookie is httpOnly, so only the server can clear it - a script
            // overwriting the name is blocked outright, not silently downgraded.
            await apiPost("/v1/users/logout");
        } catch {
            // Already-expired session, or the server is unreachable. Either way the local
            // state should still drop to signed out rather than stranding the UI.
        }
        sessionStorage.clear();
        setUsername(null);
        setProfilePictureUrl(null);
        setStatus("unauthenticated");
    }, []);

    // Runs unconditionally, including for a first-time visitor: asking the server is now
    // the only way to find out whether a session exists. A rejected /me means no valid
    // cookie, which is the signed-out answer rather than an error.
    useEffect(() => {
        let cancelled = false;
        fetchCurrentUser()
            .then(user => {
                if (cancelled) return;
                setUsername(user.username);
                setProfilePictureUrl(user.profilePictureUrl || null);
                setStatus("authenticated");
            })
            .catch(() => {
                if (cancelled) return;
                setUsername(null);
                setProfilePictureUrl(null);
                setStatus("unauthenticated");
            });
        return () => { cancelled = true; };
    }, [refresh]);

    const value = useMemo(
        () => ({
            status,
            isLoggedIn: status === "authenticated",
            username,
            profilePictureUrl,
            onAuthenticated,
            logout,
        }),
        [status, username, profilePictureUrl, onAuthenticated, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used inside an AuthProvider");
    }
    return context;
}
